import React from 'react';
import { getRequestBodySchema } from '../../../../common/src/routes/openApiSchemaHelpers';
import {
    ZxyOpenApiPathsKeys,
    ZxyOpenApiMethodsKeys,
    ZxyOpenApiPathAndOperationParameter,
    RequestBody,
} from '../../../../common/src/routes/openApiTypes';
import { assembleOpenApiUrl } from '../../store/openApiFetch';
import {
    ValidatingFormInputText,
    ValidatingFilterDropdown,
    ValidatingNumberFilterDropdown,
    ValidatingFormTextArea,
    ValidatingFormInputDateAsString,
    ValidatingAssetTemplateList,
    ValidatingAssetDropzone,
    ElementPropsWithTypedName,
    ValidatingFormInputNumber,
    FilterPropertiesOnType,
    ValidatingFormDependentSection,
} from '../validatingElements';
import { ValidatingJsonSchemaForm } from './ValidatingJsonSchemaForm';

export function getOpenApiRouteItemUrlValidatingElements<
    Path extends ZxyOpenApiPathsKeys,
    OpenApiMethod extends ZxyOpenApiMethodsKeys<Path>,
    PathMethodParameters extends ZxyOpenApiPathAndOperationParameter<Path, OpenApiMethod>,
>(path: Path, method: OpenApiMethod) {
    return {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ItemUrlForm: (props: {
            children: React.ReactNode;
            defaultItem?: Partial<RequestBody<Path, OpenApiMethod>>;
            routeParams: PathMethodParameters;
            onSubmitted?: (id: string) => Promise<void>;
        }) => {
            const itemUrl = assembleOpenApiUrl(path, method, props.routeParams);
            return (
                <ValidatingJsonSchemaForm
                    createSchema={getRequestBodySchema(path, method)}
                    updateSchema={getRequestBodySchema(path, method)}
                    itemUrl={itemUrl}
                    id={itemUrl}
                    showSavedToast
                    defaultItem={props.defaultItem}
                    onSubmitted={props.onSubmitted}
                >
                    {props.children}
                </ValidatingJsonSchemaForm>
            );
        },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        CreateUrlForm: (props: {
            children: React.ReactNode;
            defaultItem?: Partial<RequestBody<Path, OpenApiMethod>>;
            routeParams: PathMethodParameters;
            onSubmitted?: (id: string) => Promise<void>;
        }) => {
            const itemUrl = assembleOpenApiUrl(path, method, props.routeParams);
            return (
                <ValidatingJsonSchemaForm
                    createSchema={getRequestBodySchema(path, method)}
                    updateSchema={getRequestBodySchema(path, method)}
                    itemUrl={itemUrl}
                    createUrl={itemUrl}
                    showSavedToast
                    defaultItem={props.defaultItem}
                    onSubmitted={props.onSubmitted}
                >
                    {props.children}
                </ValidatingJsonSchemaForm>
            );
        },

        // eslint-disable-next-line @typescript-eslint/naming-convention
        InputText: (
            props: ElementPropsWithTypedName<typeof ValidatingFormInputText, FilterPropertiesOnType<RequestBody<Path, OpenApiMethod>, string>>,
        ) => <ValidatingFormInputText {...props} />,

        // eslint-disable-next-line @typescript-eslint/naming-convention
        InputNumber: (
            props: ElementPropsWithTypedName<typeof ValidatingFormInputNumber, FilterPropertiesOnType<RequestBody<Path, OpenApiMethod>, number>>,
        ) => <ValidatingFormInputNumber {...props} />,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        FilterDropdown: (
            props: ElementPropsWithTypedName<typeof ValidatingFilterDropdown, FilterPropertiesOnType<RequestBody<Path, OpenApiMethod>, any>>, // TODO: string and enum
        ) => <ValidatingFilterDropdown {...props} />,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        NumberFilterDropdown: (
            props: ElementPropsWithTypedName<typeof ValidatingNumberFilterDropdown, FilterPropertiesOnType<RequestBody<Path, OpenApiMethod>, number>>,
        ) => <ValidatingNumberFilterDropdown {...props} />,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        TextArea: (
            props: ElementPropsWithTypedName<typeof ValidatingFormTextArea, FilterPropertiesOnType<RequestBody<Path, OpenApiMethod>, string>>,
        ) => <ValidatingFormTextArea {...props} />,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        DependentSection: (props: { children: React.ReactNode; showIf: (item: RequestBody<Path, OpenApiMethod>) => boolean }) => (
            <ValidatingFormDependentSection {...props} />
        ),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        InputDate: (
            props: ElementPropsWithTypedName<
                typeof ValidatingFormInputDateAsString,
                FilterPropertiesOnType<RequestBody<Path, OpenApiMethod>, string>
            >,
        ) => <ValidatingFormInputDateAsString {...props} />,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        AssetTemplateList: (
            props: ElementPropsWithTypedName<
                typeof ValidatingAssetTemplateList,
                FilterPropertiesOnType<RequestBody<Path, OpenApiMethod>, { assetId: string }[]>
            >,
        ) => <ValidatingAssetTemplateList {...props} />,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        AssetDropzone: (
            props: ElementPropsWithTypedName<
                typeof ValidatingAssetDropzone,
                FilterPropertiesOnType<RequestBody<Path, OpenApiMethod>, { assetId: string }[]>
            >,
        ) => <ValidatingAssetDropzone {...props} />,
    };
}
